import { forwardRef, useEffect, useState } from 'react'
import { SafeNumberInputProps } from 'almanac-ui/TextInputField/Types'
import { useObjectRef } from '@react-aria/utils'
import { Input } from './Input'

const FLOAT_REGEX = /^-?\d*(\.\d*)?$/

const roundToPrecision = (value: number, precision: number) => {
  const factor = Math.pow(10, precision)
  return Math.round(value * factor) / factor
}

export const SafeNumberInput = forwardRef<HTMLTextAreaElement | HTMLInputElement, SafeNumberInputProps>(
  ({ onChange: _onChange, minValue, maxValue, step = 1, decimalPrecision = 1, ...rest }, fRef) => {
    const value = rest.value !== undefined ? roundToPrecision(rest.value, decimalPrecision) : undefined
    const [innerValue, setInnerValue] = useState(`${value ?? ''}`)

    const updateValue = (val: number) => {
      if (minValue != null && val < minValue) {
        setInnerValue(`${minValue}`)
        return _onChange(minValue)
      }
      if (maxValue != null && val > maxValue) {
        setInnerValue(`${maxValue}`)
        return _onChange(maxValue)
      }
      return _onChange(val)
    }

    const commitValue = () => {
      if (innerValue === '-') {
        setInnerValue('0')
        return
      }
      if (innerValue === '') {
        setInnerValue(`${value}`)
        return
      }
      const newValue = roundToPrecision(parseFloat(innerValue), decimalPrecision)
      updateValue(newValue)
      setInnerValue(`${newValue}`)
    }

    useEffect(() => {
      setInnerValue(`${value ?? ''}`)
    }, [value])

    const ref = useObjectRef(fRef)

    return (
      <Input
        {...rest}
        value={innerValue}
        ref={ref as any}
        onBlur={commitValue}
        onChange={(e) => {
          const newVal = e.currentTarget.value
          if (value === 0 && newVal === '0-') {
            setInnerValue('-')
          }
          if (newVal.match(FLOAT_REGEX)) {
            setInnerValue(newVal)
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commitValue()
          }

          let bump = e.shiftKey ? step * 10 : step

          if (maxValue && e.key === 'ArrowUp' && innerValue === '') {
            updateValue(maxValue)
          }

          if (minValue && e.key === 'ArrowDown' && innerValue === '') {
            updateValue(minValue)
          }

          if (e.key === 'ArrowUp' && innerValue !== '') {
            updateValue(parseFloat(innerValue) + bump)
          }

          if (e.key === 'ArrowDown' && innerValue !== '') {
            updateValue(parseFloat(innerValue) - bump)
          }
        }}
      />
    )
  },
)
